import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  HiOutlineClock,
  HiOutlineInformationCircle,
  HiOutlineSparkles,
  HiOutlineUsers,
  HiOutlineX,
} from "react-icons/hi";
import { toast } from "react-toastify";
import { formatDateTimeInClientTimezone } from "Utils/utils";
import { apiGetRequest } from "helpers/Requests";
import CustomButton from "components/common/Button/CustomButton";
import { URL_ROSTERING_TIMESHEET_AI_ANOMALY } from "store/services/URL";
import {
  collectAllChecksFromReport,
  computeComplianceCheckPercent,
  filterPredictionsForFundingScope,
  formatComplementaryReadinessPercents,
  formatReportSummaryParagraphs,
  isDevFallbackAiContent,
  isFundingAiEnabled,
  normalizeAiReportPayload,
  resolveAiOverallUiState,
  resolveTimesheetAiErrorMessage,
  stripAiMarkdown,
  summarizeAiChecks,
} from "../utils/timesheetAiUtils";
import { useBodyScrollLock } from "../utils/bodyScrollLock";
import { AI_DRAWER_TRANSITION_MS } from "../utils/detailPanelScroll";
import {
  ReportCheckTriage,
  ShowDetailsToggle,
} from "./ReportCheckTriage";

const DRAWER_TRANSITION_MS = AI_DRAWER_TRANSITION_MS;

const HERO_BADGE = {
  emerald: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-900",
  red: "bg-red-100 text-red-800",
  gray: "bg-slate-100 text-slate-700",
};

const HERO_SHELL = {
  emerald: "border-emerald-200/70 bg-gradient-to-br from-emerald-50/80 to-white",
  amber: "border-amber-200/70 bg-gradient-to-br from-amber-50/80 to-white",
  red: "border-red-200/70 bg-gradient-to-br from-red-50/80 to-white",
  gray: "border-slate-200 bg-gradient-to-br from-slate-50/80 to-white",
};

const RING_STROKE = {
  emerald: { track: "stroke-emerald-100", fill: "stroke-emerald-500" },
  amber: { track: "stroke-amber-100", fill: "stroke-amber-500" },
  red: { track: "stroke-red-100", fill: "stroke-red-500" },
  gray: { track: "stroke-slate-100", fill: "stroke-slate-400" },
};

const ReadinessRing = ({ percentLabel, tone = "emerald" }) => {
  const stroke = RING_STROKE[tone] || RING_STROKE.gray;
  const numeric = parseInt(String(percentLabel || "").replace("%", ""), 10);
  const value = Number.isFinite(numeric) ? Math.min(100, Math.max(0, numeric)) : 0;
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
      <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 64 64">
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          className={stroke.track}
          strokeWidth="5"
        />
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          className={stroke.fill}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="relative text-center">
        <p className="text-base font-bold tabular-nums leading-none text-slate-900">
          {percentLabel || "—"}
        </p>
        <p className="mt-px text-[8px] font-bold uppercase tracking-wider text-slate-400">
          Ready
        </p>
      </div>
    </div>
  );
};

const StatCell = ({ label, value, tone = "slate" }) => {
  const valueTone = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
  };
  return (
    <div className="min-w-0 flex-1 border-l border-slate-200/80 px-3 first:border-l-0 first:pl-0">
      <p className="truncate text-[9px] font-bold uppercase tracking-wide text-gray-600">
        {label}
      </p>
      <p
        className={`mt-0.5 truncate text-sm font-bold tabular-nums ${valueTone[tone] || valueTone.slate}`}
      >
        {value}
      </p>
    </div>
  );
};

const ReportSummaryBlock = ({ summary, compact = false }) => {
  const paragraphs = useMemo(
    () => formatReportSummaryParagraphs(summary),
    [summary]
  );

  if (!paragraphs.length) return null;

  return (
    <section
      className={`rounded-xl border border-[#C7D7F5]/70 bg-gradient-to-br from-[#F8FAFF] to-white ${
        compact ? "px-3 py-2.5" : "px-3.5 py-3"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <HiOutlineSparkles className="h-3.5 w-3.5 text-[#4B6CB7]" />
        <p className="text-[9px] font-bold uppercase tracking-wide text-gray-600">
          NDIS approver summary
        </p>
      </div>
      <div className="mt-2 space-y-2">
        {paragraphs.map((paragraph, index) => (
          <p
            key={`summary-p-${index}`}
            className="text-xs leading-relaxed text-slate-700"
          >
            {paragraph}
          </p>
        ))}
      </div>
    </section>
  );
};

const CollapsibleSection = ({
  title,
  subtitle,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50/60"
      >
        <div className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">
            {title}
          </p>
          {subtitle ? (
            <p className="mt-px truncate text-xs font-medium text-slate-700">
              {subtitle}
            </p>
          ) : null}
        </div>
        <span className="rounded-md px-2 py-1.5 ring-1 ring-inset ring-gray-300 shrink-0 text-[12px] font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-700">
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open ? (
        <div className="border-t border-slate-100 px-3 py-2">{children}</div>
      ) : null}
    </section>
  );
};

const TimesheetAiReportDrawer = ({
  open,
  timesheetId,
  timesheetLabel,
  timesheetParticipants = [],
  initialStatus,
  canReAnalyze,
  canNavigateToTab = false,
  analyseInFlight = false,
  onClose,
  onReAnalyzeComplete,
  onStreamReAnalyze,
  onNavigateToTab,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reAnalyzing, setReAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useBodyScrollLock(mounted, {
    nestedScrollAllowSelector: "[data-scroll-lock-allow]",
  });

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }

    setVisible(false);
    const timer = setTimeout(() => setMounted(false), DRAWER_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open || !timesheetId) {
      setReport(null);
      setError(null);
      setShowDetails(false);
      return undefined;
    }

    let cancelled = false;
    const loadReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiGetRequest(
          URL_ROSTERING_TIMESHEET_AI_ANOMALY(timesheetId)
        );
        const payload = response?.data?.data ?? response?.data ?? {};
        const headline = stripAiMarkdown(
          payload?.overall?.headline || payload?.reasoning || ""
        );
        if (isDevFallbackAiContent(headline)) {
          if (!cancelled) {
            setReport(null);
            setError({
              message: resolveTimesheetAiErrorMessage(
                { code: "AI_DEV_FALLBACK" },
                "AI analysis is not available."
              ),
            });
          }
          return;
        }
        if (!cancelled) {
          setReport(payload);
        }
      } catch (err) {
        if (!cancelled) {
          setReport(null);
          setError({
            message: resolveTimesheetAiErrorMessage(
              err,
              "Unable to load AI report."
            ),
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [open, timesheetId]);

  const handleReAnalyze = async () => {
    if (!canReAnalyze || !timesheetId) return;
    if (!onStreamReAnalyze) {
      toast.error("Re-analyse is not available.");
      return;
    }
    setReAnalyzing(true);
    try {
      const payload = await onStreamReAnalyze(timesheetId);
      if (payload) {
        setReport(payload);
        setError(null);
        onReAnalyzeComplete?.(timesheetId, payload);
      }
    } catch (err) {
      if (!err?.toastShown) {
        toast.error(err?.message || "Failed to re-run AI analysis.");
      }
    } finally {
      setReAnalyzing(false);
    }
  };

  const normalized = useMemo(
    () => normalizeAiReportPayload(report),
    [report]
  );

  const allChecks = useMemo(
    () => collectAllChecksFromReport(normalized),
    [normalized]
  );

  const checkSummary = useMemo(() => summarizeAiChecks(allChecks), [allChecks]);

  const complianceCheck = useMemo(
    () => computeComplianceCheckPercent(allChecks),
    [allChecks]
  );

  const scopedPredictions = useMemo(
    () => filterPredictionsForFundingScope(normalized?.predictions, normalized),
    [normalized]
  );

  const showPredictionsSection = useMemo(() => {
    if (complianceCheck?.label) return true;
    if (!scopedPredictions || typeof scopedPredictions !== "object") return false;
    return Object.values(scopedPredictions).some(Boolean);
  }, [complianceCheck, scopedPredictions]);

  const uiState = useMemo(
    () =>
      resolveAiOverallUiState({
        overall: normalized?.overall,
        checks: allChecks,
        anomaly_score: normalized?.anomaly_score,
      }),
    [normalized, allChecks]
  );

  const scorePercents = useMemo(
    () =>
      formatComplementaryReadinessPercents(
        normalized?.overall?.readiness_score
      ),
    [normalized?.overall?.readiness_score]
  );

  const handleGoToTab = (tabId, tabLabel) => {
    if (!canNavigateToTab) {
      toast.info(
        `Select this timesheet in the list to open ${tabLabel}.`
      );
      return;
    }
    onNavigateToTab?.(timesheetId, tabId);
  };

  if (!mounted) return null;

  const analyzedAt = normalized?.analyzed_at;
  const participantCount = normalized?.participants?.length || 0;
  const tone = uiState.tone || "gray";
  const attentionCount = checkSummary.critical + checkSummary.review;

  return createPortal(
    <div
      className="fixed inset-0 z-[10080] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-report-drawer-title"
      data-scroll-lock-allow
    >
      <button
        type="button"
        className={`absolute inset-0 bg-slate-900/45 backdrop-blur-[2px] transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
        aria-label="Close AI report"
      />

      <aside
        className={`relative flex h-full min-h-0 w-full max-w-xl flex-col overflow-hidden border-l border-slate-200 bg-[#F8F9FB] shadow-2xl transition-transform duration-300 ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#4B6CB7] to-[#293F6A] text-white">
              <HiOutlineSparkles className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <h2
                id="ai-report-drawer-title"
                className="truncate text-sm font-bold text-slate-900"
              >
                AI Analyse Report
              </h2>
              {timesheetLabel ? (
                <p className="truncate text-[11px] text-slate-500">
                  {timesheetLabel}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <HiOutlineX className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-3.5">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16">
              <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-blue-100 border-t-[#4B6CB7]" />
              <p className="text-xs text-slate-500">Loading AI Report…</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800">
              <p className="font-bold">Analysis unavailable</p>
              <p className="mt-0.5">{error.message}</p>
            </div>
          ) : normalized ? (
            <div className="space-y-3">
              {normalized.stale ? (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                  <HiOutlineInformationCircle className="mt-px h-3.5 w-3.5 shrink-0" />
                  <p>Timesheet changed — re-analyse for up-to-date findings.</p>
                </div>
              ) : null}

              {/* Hero */}
              <div
                className={`rounded-xl border p-3.5 ${HERO_SHELL[tone] || HERO_SHELL.gray}`}
              >
                <div className="flex items-start gap-3.5">
                  <ReadinessRing
                    percentLabel={scorePercents.readiness}
                    tone={tone}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-bold leading-tight text-slate-900">
                        {uiState.label}
                      </p>
                      <span
                        className={`shrink-0 rounded px-1.5 py-px text-[9px] font-bold uppercase ${HERO_BADGE[tone] || HERO_BADGE.gray}`}
                      >
                        {uiState.shortLabel}
                      </span>
                    </div>
                    <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-slate-600">
                      {normalized.overall?.headline || uiState.description}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex border-t border-slate-200/70 pt-3">
                  <StatCell
                    label="Reject risk"
                    value={scorePercents.rejectRisk || "—"}
                    tone={
                      Number(normalized.overall?.readiness_score) <= 0.5
                        ? "red"
                        : "amber"
                    }
                  />
                  <StatCell
                    label={attentionCount > 0 ? "Attention" : "Passed"}
                    value={
                      attentionCount > 0
                        ? attentionCount
                        : `${checkSummary.pass}/${checkSummary.evaluated || checkSummary.total}`
                    }
                    tone={
                      checkSummary.critical > 0
                        ? "red"
                        : checkSummary.review > 0
                          ? "amber"
                          : "emerald"
                    }
                  />
                  <StatCell
                    label="Risk"
                    value={normalized.overall?.risk_level || "—"}
                    tone={
                      normalized.overall?.risk_level === "high"
                        ? "red"
                        : normalized.overall?.risk_level === "medium"
                          ? "amber"
                          : "emerald"
                    }
                  />
                </div>

                {analyzedAt ? (
                  <div className="mt-2 flex flex-wrap items-center gap-x-2.5 text-[10px] text-slate-400">
                    <span className="inline-flex items-center gap-1 text-gray-600">
                      <HiOutlineClock className="h-3 w-3" />
                      {formatDateTimeInClientTimezone(analyzedAt)}
                    </span>
                    {participantCount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-gray-600">
                        <HiOutlineUsers className="h-3 w-3" />
                        {participantCount} participant
                        {participantCount === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {normalized.summary ? (
                <ReportSummaryBlock summary={normalized.summary} />
              ) : null}

              <ShowDetailsToggle
                enabled={showDetails}
                onChange={setShowDetails}
                disabled={loading}
              />

              <ReportCheckTriage
                checks={allChecks}
                showDetails={showDetails}
                canNavigate={canNavigateToTab}
                onGoToTab={handleGoToTab}
              />

              {normalized.suggestions?.length > 0 ? (
                <CollapsibleSection
                  title="Suggested actions"
                  subtitle={`${normalized.suggestions.length} before approval`}
                  defaultOpen={showDetails}
                >
                  <ol className="space-y-1.5">
                    {normalized.suggestions.map((item, index) => (
                      <li
                        key={`${item}-${index}`}
                        className="flex items-start gap-2 text-xs text-slate-700"
                      >
                        <span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#4B6CB7] text-[9px] font-bold text-white">
                          {index + 1}
                        </span>
                        <span className="leading-snug">{item}</span>
                      </li>
                    ))}
                  </ol>
                </CollapsibleSection>
              ) : null}

              {showPredictionsSection ? (
                <CollapsibleSection
                  title="Predictions"
                  subtitle="Compliance & approval outlook"
                  defaultOpen={!showDetails}
                >
                  <div className="space-y-1.5">
                    {complianceCheck?.label ? (
                      <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                        <p className="text-[9px] font-bold uppercase text-slate-400">
                          Compliance
                        </p>
                        <p
                          className={`text-lg font-bold tabular-nums ${
                            complianceCheck.percent >= 90
                              ? "text-emerald-700"
                              : complianceCheck.percent >= 70
                                ? "text-amber-700"
                                : "text-red-700"
                          }`}
                        >
                          {complianceCheck.label}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          {complianceCheck.detail}
                        </p>
                      </div>
                    ) : null}
                    {scopedPredictions.approval_readiness ? (
                      <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                        <p className="text-[9px] font-bold uppercase text-slate-400">
                          Approval readiness
                        </p>
                        <p className="mt-0.5 text-xs text-slate-700">
                          {scopedPredictions.approval_readiness}
                        </p>
                      </div>
                    ) : null}
                    {scopedPredictions.billing_risk ? (
                      <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                        <p className="text-[9px] font-bold uppercase text-slate-400">
                          Billing risk
                        </p>
                        <p className="mt-0.5 text-xs text-slate-700">
                          {scopedPredictions.billing_risk}
                        </p>
                      </div>
                    ) : null}
                    {isFundingAiEnabled(normalized) &&
                    scopedPredictions.funding_impact ? (
                      <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                        <p className="text-[9px] font-bold uppercase text-slate-400">
                          Funding impact
                        </p>
                        <p className="mt-0.5 text-xs text-slate-700">
                          {scopedPredictions.funding_impact}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </CollapsibleSection>
              ) : null}

              {!normalized.checks?.length && normalized.reasoning ? (
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[9px] font-bold uppercase text-slate-400">
                    Legacy summary
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-700">
                    {normalized.reasoning}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 bg-white px-4 py-3">
          <p className="min-w-0 max-w-[50%] text-[10px] leading-snug text-slate-400">
            AI-generated — verify before approving.
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            <CustomButton
              type="button"
              variant="secondary"
              label="Close"
              clickHandler={onClose}
              cssClass="!min-w-[4.5rem] !py-1.5 justify-center whitespace-nowrap !text-xs"
            />
            {canReAnalyze ? (
              <CustomButton
                type="button"
                variant="primary"
                label={reAnalyzing ? "Analysing…" : "Re-analyse"}
                Icon={
                  !reAnalyzing ? (
                    <HiOutlineSparkles className="h-3.5 w-3.5" />
                  ) : null
                }
                clickHandler={handleReAnalyze}
                isDisabled={reAnalyzing || loading || analyseInFlight}
                showLoading={reAnalyzing}
                cssClass="!min-w-[5.5rem] !py-1.5 justify-center whitespace-nowrap !text-xs"
              />
            ) : null}
          </div>
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default TimesheetAiReportDrawer;
